import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, Sparkles, Clock, CheckCircle2 } from "lucide-react";
import { AmbientOrb } from "./Ambient";
import { PHONE } from "@/lib/data";

export const CTABanner = () => (
  <section id="get-started" className="relative bg-white px-5 py-10 lg:px-8 md:py-14" data-testid="cta-section">
    <div className="mx-auto max-w-7xl">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-navy-900 px-6 py-8 sm:px-10 sm:py-10 md:px-12 md:py-12">
        {/* Decor */}
        <AmbientOrb className="-right-16 -top-16" size="h-64 w-64" blur="blur-3xl" color="bg-gold-500/20" duration={10} />
        <AmbientOrb className="-bottom-24 -left-10" size="h-64 w-64" blur="blur-3xl" color="bg-emerald2-500/15" duration={12} delay={0.8} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.04] grain-overlay" />
        {/* Dot-grid texture */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.14)_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_80%_80%_at_60%_50%,black_10%,transparent_75%)]" />

        <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Left Column: Headline & Action */}
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-400"
            >
              <Sparkles className="h-3.5 w-3.5" /> Let's talk
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl lg:text-[2.5rem] lg:leading-[1.15]"
            >
              Let's Build Your Business Together
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-3.5 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
            >
              Talk to our compliance experts today. Free 15-minute consultation, honest advice, no obligation.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-6 flex flex-wrap items-center gap-3.5"
            >
              <Link
                to="/contact"
                data-testid="cta-book"
                className="group inline-flex items-center gap-2 rounded-full bg-gold-500 px-6 py-3 text-sm font-semibold text-navy-900 shadow-[0_8px_24px_rgba(212,162,76,0.35)] transition-all duration-300 hover:scale-105 hover:bg-gold-400"
              >
                Book Free Consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                data-testid="cta-phone"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.03] px-5 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:border-gold-400 hover:text-gold-400"
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </motion.div>
          </div>

          {/* Right Column: Highlights Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 backdrop-blur-md">
              <p className="text-xs font-bold uppercase tracking-wider text-gold-400">What to expect in 15 mins</p>
              <ul className="mt-4 space-y-3 text-xs sm:text-sm text-white/80">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald2-400" />
                  <span>Right entity structure guidance (Pvt Ltd, LLP or OPC)</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald2-400" />
                  <span>Transparent roadmap with upfront fees & timeline</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald2-400" />
                  <span>Direct advisory with qualified CA & CS professionals</span>
                </li>
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-[11px] text-white/50">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-gold-400" /> 100% Confidential
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-gold-400" /> Zero Obligation
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);
